// src/pages/PublicSector/PublicSectorUseCases.jsx
import React, { useEffect } from "react";
import SEO from "../../components/SEO";

const track = (name, props = {}) => {
  if (typeof window !== "undefined" && window.clarity) {
    window.clarity("event", name, props);
  }
};

const PublicSectorUseCases = () => {
  useEffect(() => {
    track("public_sector_page_view", { page: "use_cases" });
  }, []);

  return (
    <main className="bg-white">
      <SEO
        title="Public Sector Use Cases | myBOQ (MH PWD SSR)"
        description="Explore public sector use cases for myBOQ — Maharashtra PWD SSR-aligned estimation workflows for PWD divisions, municipal bodies, consultants/contractors, and bank estimate documentation."
        keywords="myBOQ public sector use cases, Maharashtra PWD SSR, government estimation software, PWD estimate, municipal estimation, bank estimate, schedule b marathi"
        image="https://myboq.in/ssrlogo3.png"
        url="https://myboq.in/public-sector/use-cases"
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1B3A] via-[#0A1020] to-black" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-20 py-14 sm:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 text-white border border-white/10 px-3 py-1 rounded-full text-xs font-semibold">
              🇮🇳 <span>Currently Supporting</span>
              <span className="text-orange-300">Maharashtra PWD SSR</span>
            </div>

            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Public Sector Use Cases
              <span className="text-orange-400"> — Maharashtra PWD Estimation Digitized</span>
            </h1>

            <p className="mt-4 text-white/80 text-base sm:text-lg leading-relaxed">
              Structured SSR-based estimation workflows designed for engineers, consultants, institutions,
              and infrastructure stakeholders — from estimation to export-ready reports.
            </p>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="max-w-7xl mx-auto px-6 lg:px-20 py-12 sm:py-14">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* LEFT NAV / SUMMARY */}
          <aside className="lg:col-span-4">
            <div className="sticky top-24 rounded-2xl border border-gray-200 bg-white shadow-sm p-5">
              <div className="text-sm font-semibold text-gray-900">On this page</div>
              <ul className="mt-3 space-y-2 text-sm">
                {[
                  { id: "pwd", label: "PWD (Maharashtra)" },
                  { id: "ulb", label: "Municipal Bodies / ULBs" },
                  { id: "contractors", label: "Consultants & Contractors" },
                  { id: "banks", label: "Bank Estimate Use" },
                ].map((i) => (
                  <li key={i.id}>
                    <a
                      href={`#${i.id}`}
                      className="text-gray-600 hover:text-orange-600 hover:underline"
                      onClick={() => track("public_sector_toc_click", { page: "use_cases", section: i.id })}
                    >
                      {i.label}
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mt-5 rounded-xl bg-orange-50 border border-orange-100 p-4">
                <div className="text-sm font-semibold text-gray-900">Implementation fit</div>
                <p className="mt-1 text-sm text-gray-700">
                  myBOQ is optimized for SSR-driven workflows and standard outputs. Future departments & states can be
                  added as new catalogs.
                </p>
              </div>
            </div>
          </aside>

          {/* RIGHT CONTENT */}
          <div className="lg:col-span-8 space-y-10">
            {/* Card template */}
            <div id="pwd" className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-7">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900">
                    Public Works Departments (PWD – Maharashtra)
                  </h2>
                  <p className="mt-2 text-gray-600">
                    Standardize estimate preparation across divisions with SSR-aligned structure and consistent reports.
                  </p>
                </div>
                <span className="hidden sm:inline-flex items-center rounded-full bg-[#0B1B3A] text-white px-3 py-1 text-xs font-semibold">
                  Use Case 01
                </span>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                  <div className="text-sm font-semibold text-gray-900">The challenge</div>
                  <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Manual Excel-based estimation workflows</li>
                    <li>Inconsistent rate analysis formats</li>
                    <li>Repetitive lead calculations</li>
                    <li>Revision confusion and weak traceability</li>
                    <li>Compliance clarity challenges for junior engineers</li>
                  </ul>
                </div>

                <div className="rounded-xl bg-white border border-gray-200 p-4">
                  <div className="text-sm font-semibold text-gray-900">How myBOQ is applied</div>
                  <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>SSR item-based estimate creation</li>
                    <li>Auto rate analysis generation</li>
                    <li>Structured recap sheet output</li>
                    <li>Lead calculation automation</li>
                    <li>Royalty & testing integration</li>
                  </ul>
                </div>
              </div>

              <div className="mt-5 rounded-xl bg-[#0B1B3A]/5 border border-[#0B1B3A]/10 p-4">
                <div className="text-sm font-semibold text-gray-900">Operational impact</div>
                <div className="mt-2 grid sm:grid-cols-3 gap-3">
                  {[
                    "Standardized outputs across engineers",
                    "Reduced manual arithmetic errors",
                    "Faster internal review cycles",
                  ].map((t) => (
                    <div key={t} className="rounded-lg bg-white border border-gray-200 p-3 text-sm text-gray-700">
                      {t}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div id="ulb" className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-7">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900">
                    Municipal Corporations & Local Bodies (ULBs)
                  </h2>
                  <p className="mt-2 text-gray-600">
                    Uniform reporting and repeatable estimation workflow for multi-project environments.
                  </p>
                </div>
                <span className="hidden sm:inline-flex items-center rounded-full bg-[#0B1B3A] text-white px-3 py-1 text-xs font-semibold">
                  Use Case 02
                </span>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                  <div className="text-sm font-semibold text-gray-900">Common gaps</div>
                  <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Multi-project workload and staff turnover</li>
                    <li>Lack of uniform estimate formats</li>
                    <li>Manual revision duplication</li>
                    <li>Delays in tender documentation</li>
                  </ul>
                </div>

                <div className="rounded-xl bg-white border border-gray-200 p-4">
                  <div className="text-sm font-semibold text-gray-900">myBOQ implementation</div>
                  <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Uniform report templates</li>
                    <li>Role-based usage (Engineer / Consultant / Contractor)</li>
                    <li>Duplicate & revise estimate instantly</li>
                    <li>Structured measurement entries</li>
                  </ul>
                </div>
              </div>

              <div className="mt-5 rounded-xl bg-orange-50 border border-orange-100 p-4">
                <div className="text-sm font-semibold text-gray-900">Result</div>
                <div className="mt-2 grid sm:grid-cols-3 gap-3">
                  {["Consistent documentation", "Improved clarity between teams", "Faster tender-ready outputs"].map(
                    (t) => (
                      <div key={t} className="rounded-lg bg-white border border-orange-100 p-3 text-sm text-gray-700">
                        {t}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>

            <div id="contractors" className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-7">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900">
                    Government-Registered Consultants & Contractors
                  </h2>
                  <p className="mt-2 text-gray-600">
                    Faster preparation of SSR-compliant reports with structured history for repeat projects.
                  </p>
                </div>
                <span className="hidden sm:inline-flex items-center rounded-full bg-[#0B1B3A] text-white px-3 py-1 text-xs font-semibold">
                  Use Case 03
                </span>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                  <div className="text-sm font-semibold text-gray-900">Industry reality</div>
                  <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Multiple estimates per month with tight timelines</li>
                    <li>Manual tender documentation preparation</li>
                    <li>Unstructured estimate history</li>
                  </ul>
                </div>

                <div className="rounded-xl bg-white border border-gray-200 p-4">
                  <div className="text-sm font-semibold text-gray-900">myBOQ value</div>
                  <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Rapid estimate duplication & revision</li>
                    <li>Auto rate analysis generation</li>
                    <li>Schedule B (Marathi) output</li>
                    <li>Abstract & recap automation</li>
                  </ul>
                </div>
              </div>
            </div>

            <div id="banks" className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-7">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900">Bank Estimates & Documentation</h2>
                  <p className="mt-2 text-gray-600">
                    Generate structured estimate outputs commonly required for project funding and loan documentation.
                  </p>
                </div>
                <span className="hidden sm:inline-flex items-center rounded-full bg-[#0B1B3A] text-white px-3 py-1 text-xs font-semibold">
                  Use Case 04
                </span>
              </div>

              <div className="mt-6 rounded-xl bg-gray-50 border border-gray-200 p-4">
                <div className="text-sm font-semibold text-gray-900">Typical use</div>
                <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Loan documentation for infrastructure projects</li>
                  <li>Structured cost presentation for stakeholders</li>
                  <li>Consistent format for review and validation</li>
                </ul>
              </div>

              <div className="mt-5 text-sm text-gray-600">
                Note: myBOQ helps structure estimation outputs; final approvals follow each department’s official process.
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PublicSectorUseCases;